import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store';
import {
  Layout,
  Card,
  Row,
  Col,
  Steps,
  Tabs,
  Typography,
  Space,
  Button,
  Tag,
  Progress,
  Breadcrumb,
  Alert,
  Affix,
  Tooltip,
  Badge,
  Divider,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  EditOutlined,
  SaveOutlined,
  SendOutlined,
  CheckOutlined,
  CloseOutlined,
  FileTextOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  SafetyOutlined,
  SettingOutlined,
  AuditOutlined,
  RocketOutlined,
  TrophyOutlined,
} from '@ant-design/icons';

// 导入步骤组件
import DetailedAssessmentStep from './workflow-steps/DetailedAssessmentStep';
import DueDiligenceStep from './workflow-steps/DueDiligenceStep';
import ProjectApprovalStep from './workflow-steps/ProjectApprovalStep';
import IssuancePreparationStep from './workflow-steps/IssuancePreparationStep';
import ListingTradingStep from './workflow-steps/ListingTradingStep';

const { Content, Sider } = Layout;
const { Title, Text } = Typography;
const { Step } = Steps;
const { TabPane } = Tabs;

// Module key to i18n key mapping
const MODULE_KEY_MAP: Record<string, string> = {
  issuer_analysis: 'issuerAnalysis',
  financial_analysis: 'financialAnalysis',
  green_project: 'greenProjectAssessment',
  issuance_plan: 'issuancePlanDesign',
  service_requirements: 'intermediaryServiceRequirements',
  risk_management: 'riskIdentification',
  legal: 'legalDueDiligence',
  financial: 'financialDueDiligence',
  environmental: 'environmentalDueDiligence',
  technical: 'technicalDueDiligence',
  market: 'marketDueDiligence',
  risk: 'riskDueDiligence',
  approval_application: 'approvalApplication',
  resource_allocation: 'resourceAllocation',
  timeline_planning: 'timelinePlanning',
  documentation: 'documentation',
  rating_application: 'ratingApplication',
  roadshow_preparation: 'roadshowPreparation',
  pricing: 'pricingIssuance',
  listing: 'listing',
  trading_support: 'tradingSupport',
};

// 工作流配置 - using i18n keys instead of hardcoded strings
const WORKFLOW_CONFIG = {
  detailed_assessment: {
    titleKey: 'project:common.stages.detailedAssessment',
    icon: <FileTextOutlined />,
    descriptionKey:
      'project:unifiedWorkflow.stages.detailedAssessment.description',
    estimatedDays: 14,
    modules: [
      {
        key: 'issuer_analysis',
        titleKey: 'project:unifiedWorkflow.modules.issuerAnalysis',
        icon: <TeamOutlined />,
        status: 'completed',
      },
      {
        key: 'financial_analysis',
        titleKey: 'project:unifiedWorkflow.modules.financialAnalysis',
        icon: <DollarOutlined />,
        status: 'in_progress',
      },
      {
        key: 'green_project',
        titleKey: 'project:unifiedWorkflow.modules.greenProjectAssessment',
        icon: <EnvironmentOutlined />,
        status: 'pending',
      },
      {
        key: 'issuance_plan',
        titleKey: 'project:unifiedWorkflow.modules.issuancePlanDesign',
        icon: <SettingOutlined />,
        status: 'pending',
      },
      {
        key: 'service_requirements',
        titleKey:
          'project:unifiedWorkflow.modules.intermediaryServiceRequirements',
        icon: <SafetyOutlined />,
        status: 'pending',
      },
      {
        key: 'risk_management',
        titleKey: 'project:unifiedWorkflow.modules.riskIdentification',
        icon: <ExclamationCircleOutlined />,
        status: 'pending',
      },
    ],
  },
  due_diligence: {
    titleKey: 'project:common.stages.dueDiligence',
    icon: <AuditOutlined />,
    descriptionKey: 'project:unifiedWorkflow.stages.dueDiligence.description',
    estimatedDays: 21,
    modules: [
      {
        key: 'legal',
        titleKey: 'project:unifiedWorkflow.modules.legalDueDiligence',
        icon: <SafetyOutlined />,
        status: 'pending',
      },
      {
        key: 'financial',
        titleKey: 'project:unifiedWorkflow.modules.financialDueDiligence',
        icon: <DollarOutlined />,
        status: 'pending',
      },
      {
        key: 'environmental',
        titleKey: 'project:unifiedWorkflow.modules.environmentalDueDiligence',
        icon: <EnvironmentOutlined />,
        status: 'pending',
      },
      {
        key: 'technical',
        titleKey: 'project:unifiedWorkflow.modules.technicalDueDiligence',
        icon: <SettingOutlined />,
        status: 'pending',
      },
      {
        key: 'market',
        titleKey: 'project:unifiedWorkflow.modules.marketDueDiligence',
        icon: <TrophyOutlined />,
        status: 'pending',
      },
      {
        key: 'risk',
        titleKey: 'project:unifiedWorkflow.modules.riskDueDiligence',
        icon: <ExclamationCircleOutlined />,
        status: 'pending',
      },
    ],
  },
  project_approval: {
    titleKey: 'project:common.stages.projectApproval',
    icon: <CheckCircleOutlined />,
    descriptionKey:
      'project:unifiedWorkflow.stages.projectApproval.description',
    estimatedDays: 7,
    modules: [
      {
        key: 'approval_application',
        titleKey: 'project:unifiedWorkflow.modules.approvalApplication',
        icon: <FileTextOutlined />,
        status: 'pending',
      },
      {
        key: 'resource_allocation',
        titleKey: 'project:unifiedWorkflow.modules.resourceAllocation',
        icon: <TeamOutlined />,
        status: 'pending',
      },
      {
        key: 'timeline_planning',
        titleKey: 'project:unifiedWorkflow.modules.timelinePlanning',
        icon: <ClockCircleOutlined />,
        status: 'pending',
      },
    ],
  },
  issuance_preparation: {
    titleKey: 'project:common.stages.issuancePreparation',
    icon: <SettingOutlined />,
    descriptionKey:
      'project:unifiedWorkflow.stages.issuancePreparation.description',
    estimatedDays: 30,
    modules: [
      {
        key: 'documentation',
        titleKey: 'project:unifiedWorkflow.modules.documentation',
        icon: <FileTextOutlined />,
        status: 'pending',
      },
      {
        key: 'rating_application',
        titleKey: 'project:unifiedWorkflow.modules.ratingApplication',
        icon: <TrophyOutlined />,
        status: 'pending',
      },
      {
        key: 'roadshow_preparation',
        titleKey: 'project:unifiedWorkflow.modules.roadshowPreparation',
        icon: <RocketOutlined />,
        status: 'pending',
      },
    ],
  },
  listing_trading: {
    titleKey: 'project:common.stages.listingTrading',
    icon: <RocketOutlined />,
    descriptionKey: 'project:unifiedWorkflow.stages.listingTrading.description',
    estimatedDays: 14,
    modules: [
      {
        key: 'pricing',
        titleKey: 'project:unifiedWorkflow.modules.pricingIssuance',
        icon: <DollarOutlined />,
        status: 'pending',
      },
      {
        key: 'listing',
        titleKey: 'project:unifiedWorkflow.modules.listing',
        icon: <TrophyOutlined />,
        status: 'pending',
      },
      {
        key: 'trading_support',
        titleKey: 'project:unifiedWorkflow.modules.tradingSupport',
        icon: <RocketOutlined />,
        status: 'pending',
      },
    ],
  },
};

const UnifiedWorkflowPage: React.FC = () => {
  const { projectCode } = useParams<{ projectCode: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userRole = useSelector((state: RootState) => state.auth.user?.role);
  const { t } = useTranslation();

  // 当前步骤和模块
  const currentStep = searchParams.get('step') || 'detailed_assessment';
  const currentModule = searchParams.get('module') || null;

  // 项目数据
  const [projectData, setProjectData] = useState({
    project_name: '端到端测试绿色债券项目_e2e',
    project_code: projectCode,
    issuer_name: '端到端测试公司_e2e',
    current_stage: 'detailed_assessment',
    overall_status: 'active',
    progress_percentage: 25,
    bond_amount_display: '1.5亿元',
    ai_score: 85,
    risk_level: 'low',
  });

  // 步骤切换
  const handleStepChange = (step: string) => {
    setSearchParams({ step, ...(currentModule && { module: currentModule }) });
  };

  // 模块切换
  const handleModuleChange = (module: string) => {
    setSearchParams({ step: currentStep, module });
  };

  // 获取步骤状态
  const getStepStatus = (stepKey: string) => {
    if (stepKey === 'detailed_assessment') return 'process';
    if (stepKey === 'due_diligence') return 'wait';
    return 'wait';
  };

  // 获取模块状态图标
  const getModuleStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'in_progress':
        return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
      case 'pending':
        return <ExclamationCircleOutlined style={{ color: '#d9d9d9' }} />;
      default:
        return null;
    }
  };

  // 获取模块状态文本
  const getModuleStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return t('project:unifiedWorkflow.moduleStatus.completed');
      case 'in_progress':
        return t('project:unifiedWorkflow.moduleStatus.inProgress');
      case 'pending':
        return t('project:unifiedWorkflow.moduleStatus.pending');
      default:
        return '';
    }
  };

  // 渲染步骤内容
  const renderStepContent = () => {
    const stepConfig =
      WORKFLOW_CONFIG[currentStep as keyof typeof WORKFLOW_CONFIG];

    if (!stepConfig) {
      return <div>{t('project:unifiedWorkflow.stepNotExist')}</div>;
    }

    // 如果没有选择模块，显示模块概览
    if (!currentModule) {
      return (
        <Card
          title={`${t(stepConfig.titleKey)} - ${t('project:unifiedWorkflow.moduleOverview')}`}
        >
          <Row gutter={[16, 16]}>
            {stepConfig.modules.map(module => (
              <Col span={12} key={module.key}>
                <Card
                  size="small"
                  hoverable
                  onClick={() => handleModuleChange(module.key)}
                  style={{
                    cursor: 'pointer',
                    border:
                      module.status === 'in_progress'
                        ? '2px solid #1890ff'
                        : '1px solid #d9d9d9',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px',
                    }}
                  >
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background:
                          module.status === 'completed'
                            ? '#52c41a'
                            : module.status === 'in_progress'
                              ? '#1890ff'
                              : '#d9d9d9',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px',
                      }}
                    >
                      {module.icon}
                    </div>
                    <div>
                      <div style={{ fontWeight: '600' }}>
                        {t(module.titleKey)}
                      </div>
                      <Tag
                        color={
                          module.status === 'completed'
                            ? 'green'
                            : module.status === 'in_progress'
                              ? 'blue'
                              : 'default'
                        }
                      >
                        {getModuleStatusText(module.status)}
                      </Tag>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      );
    }

    // 渲染具体的步骤组件
    switch (currentStep) {
      case 'detailed_assessment':
        return (
          <DetailedAssessmentStep
            module={currentModule}
            projectCode={projectCode!}
          />
        );
      case 'due_diligence':
        return (
          <DueDiligenceStep module={currentModule} projectCode={projectCode!} />
        );
      case 'project_approval':
        return (
          <ProjectApprovalStep
            module={currentModule}
            projectCode={projectCode!}
          />
        );
      case 'issuance_preparation':
        return (
          <IssuancePreparationStep
            module={currentModule}
            projectCode={projectCode!}
          />
        );
      case 'listing_trading':
        return (
          <ListingTradingStep
            module={currentModule}
            projectCode={projectCode!}
          />
        );
      default:
        return (
          <Card>
            <Alert
              message={t('project:unifiedWorkflow.noContent')}
              description={t('project:unifiedWorkflow.noModulesAvailable')}
              type="info"
              showIcon
            />
          </Card>
        );
    }
  };

  // 渲染操作按钮
  const renderActionButtons = () => {
    return (
      <Space>
        {userRole === 'issuer' && (
          <>
            <Button icon={<SaveOutlined />}>
              {t('project:unifiedWorkflow.saveDraft')}
            </Button>
            <Button type="primary" icon={<SendOutlined />}>
              {t('project:unifiedWorkflow.submitReview')}
            </Button>
          </>
        )}

        {(userRole === 'exchange' || userRole === 'admin') && (
          <>
            <Button icon={<EyeOutlined />}>
              {t('project:unifiedWorkflow.viewDetails')}
            </Button>
            <Button type="primary" icon={<CheckOutlined />}>
              {t('project:unifiedWorkflow.approve')}
            </Button>
            <Button danger icon={<CloseOutlined />}>
              {t('project:unifiedWorkflow.reject')}
            </Button>
          </>
        )}
      </Space>
    );
  };

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Content style={{ padding: '24px' }}>
        {/* 页面头部 */}
        <div style={{ marginBottom: '24px' }}>
          <Breadcrumb style={{ marginBottom: '16px' }}>
            <Breadcrumb.Item>
              <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/admin/project-management')}
                style={{ padding: 0 }}
              >
                {t('project:unifiedWorkflow.projectManagement')}
              </Button>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{projectData.project_name}</Breadcrumb.Item>
          </Breadcrumb>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <Title level={2} style={{ margin: 0, color: '#1f2937' }}>
                🏛️ {t('project:unifiedWorkflow.greenBondIssuanceProcess')}
              </Title>
              <Text type="secondary" style={{ fontSize: '16px' }}>
                {projectData.project_code} • {projectData.issuer_name} •
                {t('project:unifiedWorkflow.currentStage')}{' '}
                {t(
                  WORKFLOW_CONFIG[currentStep as keyof typeof WORKFLOW_CONFIG]
                    ?.titleKey
                )}
              </Text>
            </div>
            <Space>
              <Tag color="blue">
                {t('project:unifiedWorkflow.progress')}{' '}
                {projectData.progress_percentage}%
              </Tag>
              <Tag color="green">
                {t('project:unifiedWorkflow.aiScore')} {projectData.ai_score}
              </Tag>
            </Space>
          </div>
        </div>

        {/* 全局流程导航 */}
        <Card style={{ marginBottom: '24px' }}>
          <Steps
            current={Object.keys(WORKFLOW_CONFIG).indexOf(currentStep)}
            size="small"
            style={{ marginBottom: '16px' }}
          >
            {Object.entries(WORKFLOW_CONFIG).map(([key, config]) => (
              <Step
                key={key}
                title={t(config.titleKey)}
                description={t('project:unifiedWorkflow.days', {
                  count: config.estimatedDays,
                })}
                status={getStepStatus(key)}
                icon={config.icon}
                onClick={() => handleStepChange(key)}
                style={{ cursor: 'pointer' }}
              />
            ))}
          </Steps>

          <div style={{ textAlign: 'center', color: '#6b7280' }}>
            <Text>
              {t('project:unifiedWorkflow.currentStep')}{' '}
              {t(
                WORKFLOW_CONFIG[currentStep as keyof typeof WORKFLOW_CONFIG]
                  ?.descriptionKey
              )}
            </Text>
          </div>
        </Card>

        <Row gutter={[24, 0]}>
          {/* 左侧导航 */}
          <Col span={6}>
            <Affix offsetTop={100}>
              <Card
                title={t('project:unifiedWorkflow.stepNavigation')}
                size="small"
                style={{ marginBottom: '16px' }}
              >
                {Object.entries(WORKFLOW_CONFIG).map(([key, config]) => (
                  <div
                    key={key}
                    onClick={() => handleStepChange(key)}
                    style={{
                      padding: '12px',
                      margin: '8px 0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      background: key === currentStep ? '#e6f7ff' : '#f9f9f9',
                      border:
                        key === currentStep
                          ? '1px solid #1890ff'
                          : '1px solid #d9d9d9',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <div
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: key === currentStep ? '#1890ff' : '#d9d9d9',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '8px',
                        fontSize: '12px',
                      }}
                    >
                      {config.icon}
                    </div>
                    <div>
                      <div
                        style={{
                          fontWeight: key === currentStep ? '600' : 'normal',
                        }}
                      >
                        {t(config.titleKey)}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {t('project:unifiedWorkflow.days', {
                          count: config.estimatedDays,
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </Card>

              {/* 模块导航 */}
              {currentStep &&
                WORKFLOW_CONFIG[
                  currentStep as keyof typeof WORKFLOW_CONFIG
                ] && (
                  <Card
                    title={t('project:unifiedWorkflow.moduleNavigation')}
                    size="small"
                    style={{ marginBottom: '16px' }}
                  >
                    {WORKFLOW_CONFIG[
                      currentStep as keyof typeof WORKFLOW_CONFIG
                    ].modules.map(module => (
                      <div
                        key={module.key}
                        onClick={() => handleModuleChange(module.key)}
                        style={{
                          padding: '8px',
                          margin: '4px 0',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          background:
                            module.key === currentModule
                              ? '#e6f7ff'
                              : 'transparent',
                          border:
                            module.key === currentModule
                              ? '1px solid #1890ff'
                              : '1px solid transparent',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {getModuleStatusIcon(module.status)}
                        <span style={{ marginLeft: '8px', fontSize: '14px' }}>
                          {t(module.titleKey)}
                        </span>
                      </div>
                    ))}
                  </Card>
                )}

              {/* 操作按钮 */}
              <Card title={t('project:unifiedWorkflow.actions')} size="small">
                <Space direction="vertical" style={{ width: '100%' }}>
                  {renderActionButtons()}
                </Space>
              </Card>
            </Affix>
          </Col>

          {/* 右侧内容 */}
          <Col span={18}>
            {/* 当前步骤信息 */}
            <Alert
              message={
                <div>
                  <strong>
                    {t(
                      WORKFLOW_CONFIG[
                        currentStep as keyof typeof WORKFLOW_CONFIG
                      ]?.titleKey
                    )}
                  </strong>
                  {currentModule &&
                    ` - ${t(WORKFLOW_CONFIG[currentStep as keyof typeof WORKFLOW_CONFIG]?.modules.find(m => m.key === currentModule)?.titleKey || '')}`}
                </div>
              }
              description={t(
                WORKFLOW_CONFIG[currentStep as keyof typeof WORKFLOW_CONFIG]
                  ?.descriptionKey
              )}
              type="info"
              showIcon
              style={{ marginBottom: '24px' }}
            />

            {/* 步骤内容 */}
            {renderStepContent()}
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default UnifiedWorkflowPage;
